import React from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { FiMove, FiEdit3, FiTrash2, FiEye } from "react-icons/fi";

const DraggableList = ({
  items,
  onReorder,
  onEdit,
  onDelete,
  onView,
  itemType = "item",
  renderContent,
  className = "",
  // Bulk selection props
  selectedItems = [],
  onItemSelect,
  bulkMode = false,
}) => {
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    // Create the order array for API
    const orderData = newItems.map((item, index) => ({
      id: item._id,
      order: index,
    }));

    onReorder(orderData, newItems);
  };

  const handleCheckboxChange = (itemId, checked) => {
    if (onItemSelect) {
      onItemSelect(itemId, checked);
    }
  };

  const isSelected = (itemId) => selectedItems.includes(itemId);

  return (
    <div className={`space-y-4 ${className}`}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`${itemType}-list`}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`space-y-3 ${
                snapshot.isDraggingOver ? "bg-gray-700/20 rounded-lg p-2" : ""
              }`}
            >
              {items.map((item, index) => (
                <Draggable key={item._id} draggableId={item._id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700 ${
                        snapshot.isDragging
                          ? "shadow-2xl scale-105 rotate-2"
                          : "hover:shadow-xl"
                      } ${
                        isSelected(item._id)
                          ? "ring-2 ring-blue-500 bg-gray-700"
                          : ""
                      }`}
                    >
                      <div className="p-4 flex items-start gap-4">
                        {/* Checkbox for bulk selection */}
                        {bulkMode && (
                          <div className="flex-shrink-0 mt-1">
                            <input
                              type="checkbox"
                              checked={isSelected(item._id)}
                              onChange={(e) =>
                                handleCheckboxChange(item._id, e.target.checked)
                              }
                              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                            />
                          </div>
                        )}

                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className="text-gray-400 hover:text-brand-gold cursor-grab active:cursor-grabbing flex-shrink-0 mt-1"
                        >
                          <FiMove size={20} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {renderContent ? (
                            renderContent(item)
                          ) : (
                            <>
                              <h3 className="text-xl font-semibold text-brand-gold mb-2 line-clamp-2">
                                {item.title}
                              </h3>
                              <p className="text-gray-300 text-sm line-clamp-3">
                                {item.content || item.description}
                              </p>
                            </>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 flex-shrink-0">
                          {onView && (
                            <button
                              onClick={() => onView(item)}
                              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 rounded-md transition-colors"
                              title="View"
                            >
                              <FiEye size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => onEdit(item)}
                            className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10 rounded-md transition-colors"
                            title="Edit"
                          >
                            <FiEdit3 size={16} />
                          </button>
                          <button
                            onClick={() => onDelete(item._id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-md transition-colors"
                            title="Delete"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default DraggableList;
